// CvoFile.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import CvoFileList from './CvoFileList';
import CvoFileInput from './CvoFileInput';
import ConfirmDialog from '../message/ConfirmDialog';
import ResponseAlert from '../message/ResponseAlert';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    LIST_CVO_FILE, ADD_CVO_FILE, EDIT_CVO_FILE, REMOVE_CVO_FILE,
	CvoFileDictionary
} from './CvoFileGql';
import { AppObject, AppNumber } from '../../model/AppConst';
import ValueUtil from '../../model/ValueUtil';
import { userInfoRepo, NoUser } from '../../model/CvoModel';

const dcPad = AppNumber.dataContainerPadding;
const CvoFileContainer = styled(Box)({
	position:"absolute", top:dcPad, right:dcPad, bottom: dcPad, left: dcPad, display:'flex'
});


const ErrorTitle =CvoFileDictionary.errorTitle;


export default function CvoFile() {
    const [cvoFileRecords, setCvoFileRecords] = useState([]);
	const [cvoFileSelected, setCvoFileSelected] = useState(null);
    const [editorState, setEditorState] = useState(null);
    const [promptToConfirm, setPromptToConfirm] = useState(null);
    const [responseAlert, setResponseAlert] = useState(null);

    // ##### Call GraphQL to get List #####
    const [getCvoFileList, responseList] = useLazyQuery(LIST_CVO_FILE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.List})}
    });
    
    // ##### GraphQL Mutation.  ###
    const [addCvoFile, responseAdd] = useMutation( ADD_CVO_FILE, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Add})}
	} );
    const [editCvoFile, responseEdit] = useMutation( EDIT_CVO_FILE, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Edit})}
	} );
    const [removeCvoFile, responseRemove] = useMutation( REMOVE_CVO_FILE, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Remove})}
	});

    useEffect(() => {
        getCvoFileList({variables:{forWhom:'all'}});
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.cvoFileList) setCvoFileRecords(data.cvoFileList);
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.cvoFileAdd.ok) {
            setEditorState(null);
            setCvoFileRecords(data.cvoFileAdd.list);
        }
        else setResponseAlert({open:true, resultData: data.cvoFileAdd, title: ErrorTitle.Add});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.cvoFileEdit.ok) {
            setEditorState(null);
            setCvoFileRecords(data.cvoFileEdit.list);
        }
        else setResponseAlert({open:true, resultData: data.cvoFileEdit, title: ErrorTitle.Edit});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.cvoFileRemove.ok) setCvoFileRecords(data.cvoFileRemove.list);
        else setResponseAlert({open:true, resultData: data.cvoFileRemove, title: ErrorTitle.Remove});
    };

    // +++++++ UI callbacks ++++++++
    const onRequestAdd = () => {
        if(responseAdd) responseAdd.reset();
        setEditorState({item: undefined}); // input component will set a default data.
    };

    const onRequestEdit = (data) => {
        if(responseEdit) responseEdit.reset();
        //getCvoFileItemToEdit({variables: {fileNid: data.fileNid}});
        setEditorState({item: data});
    };

    const onRequestRemove = (item) => {

        setPromptToConfirm({
            data: item,
            title: '파일 정보 삭제',
            messages: [
                '파일 을 삭제하시겠습니까?',
                '해당 파일의 정보는 즉시, 완전히 삭제됩니다.',
                '삭제된 정보는 복구할 수 없습니다',
                '정보 삭제를 진행하시겠습니까?'
            ],
            callback: (data) => {
                setPromptToConfirm(null);
                if(data) {
                    if(responseRemove) responseRemove.reset();
                    const param = {variables:{cvoFile:{fileNid: data.fileNid}}};
                    removeCvoFile(param);
                }
            }
        });
    };

    // Handler - Submit for mutation fired by CvoFileInput component.
    const onClickMutate = (item, isEdit) => {
        // item.custId = ''; // testing error callback.
        const param = {variables: {cvoFile: ValueUtil.refineToSubmit(item)}};
        if(isEdit) editCvoFile(param);
        else addCvoFile(param);
    };

    const onCloseEditor = () => {
        setEditorState(null);
    };


    if(ValueUtil.hasAnyAuthError(
        responseAdd, responseEdit, responseRemove, responseList
    )) userInfoRepo(NoUser);

    // ---------------------------- Render Components ----------------------------
    const renderPromptToConfirmBox = () => {
        const prompt = promptToConfirm ? promptToConfirm : {};
        return (
            <ConfirmDialog
                open={Boolean(promptToConfirm)}
                prompt={prompt}
                onClose={prompt.callback}
            />
        );
    };

    const renderEditor = () => {
        const es = editorState ? editorState : {item: undefined};

        return(
            es.item
            ?
            <CvoFileInput
                open={Boolean(editorState)}
                isEdit={true}
                item={es.item}
                responseSaving={responseEdit}
                onClickMutate={onClickMutate}
                onClose={onCloseEditor}
                />
            :
            <CvoFileInput
                open={Boolean(editorState)}
                isEdit={false}
                responseSaving={responseAdd}
                onClickMutate={onClickMutate}
                onClose={onCloseEditor}
                />
        );
    };

	// AppMain.js의 resize에 따라 창을 채우는 고정된 높이를 갖는 <ContentContainer> 안에 놓임.
    return (
        <CvoFileContainer>
            <CvoFileList
                records={cvoFileRecords}
				selected={cvoFileSelected}
				onClickItem={setCvoFileSelected}
                onRequestAdd={onRequestAdd}
                onRequestEdit={onRequestEdit}
                onRequestRemove={onRequestRemove}
            />
            {renderEditor()}
            {renderPromptToConfirmBox()}
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </CvoFileContainer>
    )
}
